/**
 * Per-transaction validation (W1-03 core-model-ops; ARCHITECTURE.md §5.2): the invariants of the records a
 * transaction touched, run on every commit. Same checks as the full `checkInvariants` (core/model/invariants.ts),
 * limited to what the ops can have broken:
 *  - every inserted or patched record (shape, identity incl. the id counter and cross-table ids, own rules);
 *  - the tracks of touched keys (old and new track of a moved key) and the line of touched tracks (gif rules);
 *  - every track owned by a variable of a touched effect or video (before and after), including each of several
 *    tracks that share one owner (the index only names the first of them);
 *  - the tracks of an inserted line and of a line whose `origin` changed (the lead-in rule depends on it);
 *  - every line of a parent cycle closed by an inserted line or a parent change (each one is reported);
 *  - lines using a touched asset (GIF rules depend on the asset), all asset paths when assets changed;
 *  - the whole tempo table when a BPM point changed; group positions when groups changed;
 *  - order lists when lines/effects were added or removed or the list was set; touched singletons;
 *  - references to deleted records (notes on a deleted line, keys of a deleted track, users of a deleted asset...).
 *
 * `doc` / `idx` are the document WITH the ops applied (a draft's `tx.doc` / `tx.idx`, or the container after apply).
 */

import { InvariantChecker } from '../model/invariants';
import type {
  AssetId,
  ChartDoc,
  Effect,
  EffectId,
  TrackOwner,
  VideoId,
  DocIndexes,
  InvariantViolation,
  Key,
  LineId,
  OrderListName,
  SingletonName,
  TableName,
  TableRecords,
  Track,
  TrackId,
} from '../model/types';
import type { Op } from './types';

type AnyRec = TableRecords[TableName];

/** The owner of an effect or video variable track. */
type VarOwner = Exclude<TrackOwner, { readonly kind: 'line' }>;

function varOwnerKey(o: VarOwner): string {
  return o.kind === 'effectVar' ? `e:${String(o.effect)}:${o.name}` : `v:${String(o.video)}:${o.which}`;
}

function getRec(doc: ChartDoc, tbl: TableName, id: AnyRec['id']): AnyRec | undefined {
  return (doc[tbl] as unknown as ReadonlyMap<AnyRec['id'], AnyRec>).get(id);
}

export function validateTouched(doc: ChartDoc, idx: DocIndexes, ops: readonly Op[]): readonly InvariantViolation[] {
  const touched = new Map<TableName, Set<AnyRec['id']>>();
  const deleted = new Map<string, { tbl: TableName; rec: AnyRec }>();
  const tracks = new Set<TrackId>();
  const lines = new Set<LineId>();
  const singletons = new Set<SingletonName>();
  const orders = new Set<OrderListName>();
  const effects = new Set<EffectId>();
  const videos = new Set<VideoId>();
  const varTracks: VarOwner[] = [];
  const originLines = new Set<LineId>();
  const parentLines = new Set<LineId>();
  let bpm = false;
  let groups = false;
  let assets = false;

  const touch = (tbl: TableName, id: AnyRec['id']): void => {
    let set = touched.get(tbl);
    if (set === undefined) {
      set = new Set();
      touched.set(tbl, set);
    }
    set.add(id);
  };

  for (const op of ops) {
    switch (op.o) {
      case 'idSeq':
        break;
      case 'single':
        singletons.add(op.key);
        break;
      case 'order':
        orders.add(op.list);
        break;
      case 'ins':
      case 'del':
      case 'set': {
        const id = op.o === 'set' ? op.id : op.rec.id;
        touch(op.tbl, id);
        if (op.o === 'del') deleted.set(`${op.tbl}:${String(id)}`, { tbl: op.tbl, rec: op.rec });
        if (op.o !== 'set') {
          if (op.tbl === 'lines') orders.add('lineOrder');
          if (op.tbl === 'effects') orders.add('effectOrder');
        }
        switch (op.tbl) {
          case 'bpm':
            bpm = true;
            break;
          case 'groups':
            groups = true;
            break;
          case 'lines': {
            const has = (f: string): boolean => op.o === 'set' && Object.prototype.hasOwnProperty.call(op.next, f);
            if (op.o === 'ins' || has('origin')) originLines.add(id as LineId);
            if (op.o === 'ins' || has('parent')) parentLines.add(id as LineId);
            break;
          }
          case 'assets':
            assets = true;
            break;
          case 'keys': {
            // The old track of a moved or deleted key must still be valid (ordering, non-empty).
            if (op.o === 'set') {
              const prevTrack = (op.prev as { track?: TrackId }).track;
              if (prevTrack !== undefined) tracks.add(prevTrack);
            } else {
              tracks.add(op.rec.track);
            }
            break;
          }
          case 'tracks': {
            // The previous owner (line gif rules, variable links of an effect or video) must still be valid.
            const o = op.o === 'set' ? (op.prev as { owner?: Track['owner'] }).owner : op.rec.owner;
            if (o?.kind === 'line') lines.add(o.line);
            else if (o?.kind === 'effectVar') effects.add(o.effect);
            else if (o?.kind === 'videoVar') videos.add(o.video);
            break;
          }
          case 'effects': {
            // Tracks owned by the effect's variables (before and after) must still match them.
            const vars = op.o === 'set' ? (op.prev as { vars?: Effect['vars'] }).vars : op.rec.vars;
            const id = op.o === 'set' ? op.id : op.rec.id;
            for (const v of vars ?? []) varTracks.push({ kind: 'effectVar', effect: id, name: v.name });
            break;
          }
          case 'videos': {
            const id = op.o === 'set' ? op.id : op.rec.id;
            varTracks.push(
              { kind: 'videoVar', video: id, which: 'alpha' },
              { kind: 'videoVar', video: id, which: 'dim' },
            );
            break;
          }
          default:
            break;
        }
        break;
      }
    }
  }

  const c = new InvariantChecker(doc, idx);

  for (const [tbl, ids] of touched) {
    for (const id of ids) {
      const rec = getRec(doc, tbl, id);
      if (rec === undefined) continue;
      c.record(tbl, rec, true);
      switch (tbl) {
        case 'keys':
          tracks.add((rec as Key).track);
          break;
        case 'tracks': {
          const o = (rec as Track).owner;
          if (o.kind === 'line') lines.add(o.line);
          else if (o.kind === 'effectVar') effects.add(o.effect);
          else videos.add(o.video);
          break;
        }
        case 'effects':
          for (const v of (rec as Effect).vars)
            varTracks.push({ kind: 'effectVar', effect: rec.id as EffectId, name: v.name });
          break;
        case 'assets':
          for (const l of idx.assetUsers(id as AssetId).lines) lines.add(l);
          break;
        default:
          break;
      }
    }
  }

  // Tracks and lines already checked through `record` are skipped below.
  // Records referring to deleted ones (only when the record is really gone at the end of the transaction).
  for (const { tbl, rec } of deleted.values()) {
    if (getRec(doc, tbl, rec.id) === undefined) c.deleted(tbl, rec);
  }

  for (const l of parentLines) for (const m of cycleThrough(doc, l)) lines.add(m);
  for (const l of originLines) {
    if (!doc.lines.has(l)) continue;
    for (const ts of idx.tracksOfLine(l).values()) for (const t of ts) tracks.add(t);
  }
  if (varTracks.length > 0) {
    // Variable tracks are few, and an effect or video edit is rare: one pass finds every track of each owner.
    const owners = new Set(varTracks.map(varOwnerKey));
    for (const t of doc.tracks.values()) {
      if (t.owner.kind !== 'line' && owners.has(varOwnerKey(t.owner))) tracks.add(t.id);
    }
  }
  const checkedTracks = touched.get('tracks');
  for (const t of tracks) {
    if (checkedTracks?.has(t) === true && doc.tracks.has(t)) continue;
    const rec = doc.tracks.get(t);
    if (rec !== undefined) c.track(rec);
  }
  const checkedLines = touched.get('lines');
  for (const l of lines) {
    if (checkedLines?.has(l) === true && doc.lines.has(l)) continue;
    const rec = doc.lines.get(l);
    if (rec !== undefined) c.line(rec);
  }
  const checkedEffects = touched.get('effects');
  for (const e of effects) {
    if (checkedEffects?.has(e) === true) continue;
    const rec = doc.effects.get(e);
    if (rec !== undefined) c.effect(rec);
  }
  const checkedVideos = touched.get('videos');
  for (const v of videos) {
    if (checkedVideos?.has(v) === true) continue;
    const rec = doc.videos.get(v);
    if (rec !== undefined) c.video(rec);
  }
  if (bpm) c.bpmTable();
  if (groups) c.groups();
  if (assets) c.assetPaths();
  for (const list of orders) c.order(list);
  for (const s of singletons) c.singleton(s);

  return dedupe(c.out);
}

/** The other lines of the parent cycle through `start` (empty when following its parents does not lead back). */
function cycleThrough(doc: ChartDoc, start: LineId): LineId[] {
  const chain: LineId[] = [];
  let cur = doc.lines.get(start)?.parent ?? null;
  for (let steps = 0; cur !== null && steps <= doc.lines.size; steps++) {
    if (cur === start) return chain;
    chain.push(cur);
    cur = doc.lines.get(cur)?.parent ?? null;
  }
  return [];
}

function dedupe(list: readonly InvariantViolation[]): readonly InvariantViolation[] {
  const seen = new Set<string>();
  const out: InvariantViolation[] = [];
  for (const v of list) {
    const k = `${v.code}|${v.table ?? ''}|${String(v.id ?? '')}|${v.message}`;
    if (seen.has(k)) continue;
    seen.add(k);
    out.push(v);
  }
  return out;
}
